import type { CategoryContent } from './row.js';

export const PHOTOGRAPHY: CategoryContent = {
  bscba: [
    [
      "Which term, coined by Henri Cartier-Bresson, describes capturing an event at its most visually and emotionally revealing instant?",
      [
        'The decisive moment',
        'The golden hour',
        'The vanishing point',
        'The rule of thirds',
      ],
      0,
      'Cartier-Bresson used "the decisive moment" for the instant when form and content align perfectly within the frame.',
    ],
    [
      "Dorothea Lange's photograph \"Migrant Mother\" became an icon of which historical period?",
      [
        'The Great Depression',
        'World War I',
        'The Gilded Age',
        'The Progressive Era',
      ],
      0,
      'Lange made the portrait in 1936 for the Farm Security Administration, documenting displaced farmworkers during the Depression.',
    ],
    [
      'Which photographer co-founded Magnum Photos, the cooperative agency that let photographers keep the rights to their own images?',
      [
        'Robert Capa',
        'Ansel Adams',
        'Man Ray',
        'Diane Arbus',
      ],
      0,
      'Capa founded Magnum in 1947 with Cartier-Bresson, David Seymour, and George Rodger, giving photojournalists creative and legal control.',
    ],
    [
      'Diane Arbus is best known for portraits of which kind of subject?',
      [
        'People on the margins of society',
        'Empty landscapes',
        'Abstract still lifes',
        'Architectural interiors',
      ],
      0,
      'Arbus photographed people society tended to look away from, including performers, disabled individuals, and nudists, with unflinching directness.',
    ],
    [
      'What genre does street photography most closely belong to?',
      [
        'Unposed scenes of everyday public life',
        'Staged portrait sittings',
        'Studio product photography',
        'Aerial landscape surveys',
      ],
      0,
      'Street photography captures spontaneous, candid moments of daily life, typically without staging or posed subjects.',
    ],
    [
      'Which museum established one of the first dedicated photography curatorial departments, helping establish the medium as fine art?',
      [
        'The Museum of Modern Art',
        'The Louvre',
        'The British Museum',
        'The Uffizi Gallery',
      ],
      0,
      'MoMA created a photography department in 1940, treating photographs as art objects worthy of curation and collection.',
    ],
    [
      'Which photographer became famous for intimate celebrity portraits for magazines like Rolling Stone and Vanity Fair?',
      [
        'Annie Leibovitz',
        'Cindy Sherman',
        'Berenice Abbott',
        'Lee Miller',
      ],
      0,
      'Leibovitz built her reputation on staged, theatrical celebrity portraiture, from John Lennon and Yoko Ono to modern film stars.',
    ],
    [
      'In composition, what does the "rule of thirds" suggest a photographer do?',
      [
        'Place key subjects along imaginary gridlines rather than dead center',
        'Always fill exactly a third of the frame with sky',
        'Divide every photograph into three separate printed panels',
        'Use only three tones: black, white, and gray',
      ],
      0,
      'The rule of thirds is a compositional guideline placing points of interest along a 3x3 grid, generally producing more dynamic images than strict centering.',
    ],
    [
      'What kind of camera captures a scene with an extremely wide field of view, sometimes over 180 degrees?',
      [
        'A panoramic camera',
        'A pinhole camera',
        'An instant camera',
        'A twin-lens reflex camera',
      ],
      0,
      "Panoramic cameras use rotating lenses or wide film formats to capture sweeping horizontal views beyond a standard lens's frame.",
    ],
    [
      'What is a "photobook," as distinct from a photography exhibition?',
      [
        'A curated sequence of photographs published as a standalone book',
        'A digital photo album stored on a phone',
        'A technical manual for camera repair',
        'A scrapbook of family snapshots',
      ],
      0,
      "A photobook is an artist's medium in its own right: sequencing, pacing, and pairing images in ways an exhibition wall never could.",
    ],
    [
      'Vivian Maier, whose work was only discovered after her death, worked professionally as a what?',
      [
        'Nanny',
        'Photojournalist',
        'Museum curator',
        'Fashion photographer',
      ],
      0,
      'Maier took over 150,000 street photographs in her spare time while working as a nanny in Chicago; her archive surfaced after her death in 2009.',
    ],
    [
      "What subject is Sebastião Salgado's photography most associated with?",
      [
        'Large-scale documentary series on labor, migration, and the environment',
        'Abstract macro photography of insects',
        'Fashion editorials for luxury brands',
        'Portraits of Hollywood celebrities',
      ],
      0,
      'Salgado is known for epic black-and-white documentary projects like "Workers" and "Genesis," examining humanity\'s relationship to land and labor.',
    ],
    [
      "A self-portrait taken by a photographer holding their own camera at arm's length is now popularly known as what?",
      [
        'A selfie',
        'A candid',
        'A portrait bust',
        'A vignette',
      ],
      0,
      'Self-portraiture in photography predates smartphones by over a century, but "selfie" became the common term for the informal handheld version.',
    ],
    [
      'Since it lacks color information, what does black-and-white photography traditionally rely on to create depth and mood?',
      [
        'Contrast and tonal range',
        'Depth of field alone',
        'Motion blur exclusively',
        'Symmetry of the subject',
      ],
      0,
      'Without color, monochrome photography depends on light, shadow, and gray tones to build mood, form, and a sense of depth.',
    ],
    [
      'William Henry Fox Talbot\'s "The Pencil of Nature," published in the 1840s, is significant for being one of the first what?',
      [
        'Commercially published books illustrated with photographs',
        'Photography instruction manuals for the public',
        'Museum exhibitions of photographic art',
        'Photography magazines with a paying readership',
      ],
      0,
      "Talbot's 1844 book paired his calotype prints with text, making it one of the earliest photographically illustrated commercial publications.",
    ],
  ],

  msc: [
    [
      "The \"New Topographics\" movement of the 1970s photographed which subject matter, breaking from romantic landscape traditions?",
      [
        'Man-altered suburban and industrial landscapes',
        'Idealized wilderness untouched by humans',
        'Studio-lit still-life arrangements',
        'Formal royal portraiture',
      ],
      0,
      'The 1975 "New Topographics" exhibition showed deadpan images of parking lots, tract housing, and industrial sites in place of Ansel Adams-style grandeur.',
    ],
    [
      'What did August Sander attempt with his project "People of the 20th Century"?',
      [
        'A systematic visual typology of German society by occupation and class',
        'A record of only aristocratic families across Europe',
        'A study focused exclusively on children and infants',
        'Candid images taken at political rallies',
      ],
      0,
      'Sander organized portraits into social categories such as farmers, tradespeople, and artists, building an ambitious sociological archive through portraiture.',
    ],
    [
      'Weegee (Arthur Fellig) built his reputation photographing what kind of subject in 1930s–40s New York?',
      [
        'Crime scenes and urban nightlife',
        'Ballet performances',
        'Botanical gardens',
        'Corporate boardrooms',
      ],
      0,
      'Weegee monitored police radio to arrive at crime scenes before anyone else, producing raw, flash-lit tabloid photography.',
    ],
    [
      "Man Ray's \"rayographs\" were created using what unconventional method?",
      [
        'Placing objects directly onto photosensitive paper without a camera',
        'Double-exposing film inside a standard camera',
        'Painting over developed prints by hand',
        'Photographing through colored glass filters',
      ],
      0,
      'Rayographs are cameraless photograms made by arranging objects on light-sensitive paper and exposing it directly to light.',
    ],
    [
      'Robert Frank\'s 1958 photobook "The Americans" was notable for portraying the United States in what way?',
      [
        'A raw, unromanticized view at odds with prevailing patriotic imagery',
        'An idealized postcard vision of American prosperity',
        'A purely architectural survey of American cities',
        'A chronological record of presidential elections',
      ],
      0,
      "Frank's grainy, off-kilter images of everyday American life shocked contemporary critics but became hugely influential on later documentary photographers.",
    ],
    [
      'William Eggleston is widely credited with legitimizing which previously dismissed photographic approach as fine art?',
      [
        'Color photography',
        'Pinhole photography',
        'Aerial photography',
        'Instant, Polaroid-style photography',
      ],
      0,
      "Before Eggleston's 1976 MoMA show, color photography was largely seen as commercial; his work helped establish it as a serious art form.",
    ],
    [
      'The f/64 Group, which included Ansel Adams and Edward Weston, championed which artistic principle?',
      [
        'Sharp, unmanipulated "straight" photography over painterly softness',
        'Exclusively abstract, blurred compositions',
        'Heavy hand-coloring of finished prints',
        'Photographing only urban subjects',
      ],
      0,
      'Named for a small aperture producing maximum sharpness, the group rejected Pictorialist manipulation in favor of clear, direct photographic form.',
    ],
    [
      "What made the discovery of Vivian Maier's negatives significant to photography history?",
      [
        'It revealed a major street photographer who never sought recognition in her lifetime',
        'It proved street photography had been invented earlier than believed',
        'It uncovered previously unknown works by Diane Arbus',
        'It settled a long dispute over the invention of color film',
      ],
      0,
      "Maier's vast, unseen archive raised questions about authorship, artistic intent, and how art history recognizes work made outside institutions.",
    ],
    [
      'Cindy Sherman\'s "Untitled Film Stills" series consists of what kind of images?',
      [
        'Sherman posing as fictional female characters from imagined films',
        'Documentary portraits of aspiring actresses',
        'Behind-the-scenes shots from real film productions',
        'Landscape photographs of Hollywood film locations',
      ],
      0,
      'Sherman photographed herself in different personas evoking mid-century film genres, exploring identity and media-constructed femininity.',
    ],
    [
      "Alexander Rodchenko's photographs are known for what distinctive visual approach tied to Constructivist ideology?",
      [
        'Dramatic, unconventional high and low camera angles',
        'Soft-focus romantic portraiture',
        'Muted, desaturated color palettes',
        'Symmetrical classical composition',
      ],
      0,
      "Rodchenko used extreme angles to disrupt conventional viewpoints, reflecting Constructivism's embrace of dynamic, industrial modernity.",
    ],
    [
      "What was distinctive about Edward Weston's photographs of vegetables, such as his famous peppers?",
      [
        'He photographed them as abstract, sculptural forms rather than food',
        'He always photographed them decaying, to symbolize mortality',
        'He shot them exclusively in color to emphasize freshness',
        'He arranged them into recognizable human portraits',
      ],
      0,
      "Weston's close, precisely lit studies transformed everyday vegetables into forms echoing the human body and classical sculpture.",
    ],
    [
      'Aperture, founded in 1952 by Ansel Adams, Dorothea Lange, and others, was created as what kind of publication?',
      [
        'A serious quarterly dedicated to photography as art',
        'A commercial camera-equipment catalog',
        'A tabloid newspaper supplement',
        'A government-issued technical bulletin',
      ],
      0,
      'Aperture magazine gave fine-art photography a dedicated critical and publishing platform distinct from commercial photo magazines.',
    ],
    [
      "Berenice Abbott's project \"Changing New York\" documented the city during what kind of transformation?",
      [
        'Rapid modernization and the demolition of older architecture',
        'Its recovery after a natural disaster',
        'Its founding as a colonial settlement',
        'A period of population decline',
      ],
      0,
      'In the 1930s, Abbott systematically photographed New York as older buildings gave way to skyscrapers, preserving a vanishing cityscape.',
    ],
    [
      'What is the main distinction generally drawn between photojournalism and documentary photography?',
      [
        'Photojournalism serves timely news reporting; documentary work often pursues longer personal projects',
        'Photojournalists work only in color and documentary photographers only in black-and-white',
        'Documentary photographers never publish their work',
        'Photojournalists exclusively photograph war zones',
      ],
      0,
      'Both record real events, but photojournalism is tied to news cycles while documentary photography often unfolds over years with a more personal frame.',
    ],
    [
      'Lee Miller transitioned from celebrated fashion model to what role during World War II?',
      [
        'War correspondent and photographer for Vogue',
        'Military intelligence officer',
        'Field nurse',
        'Radio broadcaster',
      ],
      0,
      'Miller photographed the Blitz, the liberation of Paris, and Nazi concentration camps for British Vogue, an extraordinary shift from her modeling career.',
    ],
  ],

  phd: [
    [
      'In "Camera Lucida," what does Roland Barthes call the personal, piercing detail in a photograph that affects a viewer emotionally?',
      [
        'Punctum',
        'Studium',
        'Aura',
        'Referent',
      ],
      0,
      'Barthes distinguished the "studium" (a photo\'s general cultural interest) from the "punctum," an incidental detail that wounds or pierces the viewer personally.',
    ],
    [
      'Walter Benjamin\'s essay "The Work of Art in the Age of Mechanical Reproduction" argues photographic reproduction diminishes an artwork\'s what?',
      [
        'Aura, its unique presence in time and place',
        'Market value',
        'Physical durability',
        'Color accuracy',
      ],
      0,
      'Benjamin argued mechanical reproduction strips an original artwork of its singular "aura," fundamentally changing how art is experienced and valued.',
    ],
    [
      "John Szarkowski's influential 1966 book, drawn from his MoMA curatorial practice, argued photography should be understood through which formal qualities?",
      [
        "The medium's own inherent properties, such as detail, frame, time, and vantage point",
        'Its similarity to painting composition',
        'Its role purely as scientific documentation',
        'Its commercial and advertising applications',
      ],
      0,
      'In "The Photographer\'s Eye," Szarkowski proposed photography be judged on qualities unique to the medium rather than borrowed painterly standards.',
    ],
    [
      "Susan Sontag's \"On Photography\" describes the act of photographing someone as analogous to what?",
      [
        'A form of appropriation or symbolic aggression toward the subject',
        'A purely neutral, harmless act of documentation',
        'An act indistinguishable from painting',
        'A religious ritual of remembrance',
      ],
      0,
      'Sontag argued photographing people is a subtle act of power, turning subjects into objects that can be possessed, collected, and controlled.',
    ],
    [
      'Szarkowski\'s 1967 "New Documents" exhibition at MoMA grouped which three photographers as redefining documentary work?',
      [
        'Diane Arbus, Lee Friedlander, and Garry Winogrand',
        'Ansel Adams, Edward Weston, and Paul Strand',
        'Man Ray, Alexander Rodchenko, and László Moholy-Nagy',
        'Robert Capa, David Seymour, and George Rodger',
      ],
      0,
      'The show argued these photographers used documentary style for personal expression rather than social reform, a shift Szarkowski considered pivotal.',
    ],
    [
      'Edward Steichen\'s 1955 MoMA exhibition "The Family of Man" aimed to express what universalist theme?',
      [
        'Shared human experience across all cultures and nations',
        'The technical superiority of American photographic equipment',
        'The decline of traditional portraiture',
        'The rise of abstract photography',
      ],
      0,
      "Assembling over 500 images from dozens of countries, Steichen's exhibition presented photography as evidence of a common humanity, though later critics challenged its universalizing assumptions.",
    ],
    [
      'What critique did later scholars, notably Allan Sekula, level against "The Family of Man"?',
      [
        'It flattened political and historical difference into sentimental universalism',
        'It contained no photographs by women',
        'It was funded entirely by a rival newspaper',
        'It excluded all American photographers',
      ],
      0,
      "Sekula and others argued the exhibition's humanist framing obscured real inequalities of power, class, and history behind a comforting sameness.",
    ],
    [
      'In critical writing on photography, what does "indexicality" refer to?',
      [
        "A photograph's physical, causal trace of the light that produced it",
        'The numerical catalog system used by museums',
        "The alphabetical ordering of a photographer's archive",
        'The resolution measured in the final print',
      ],
      0,
      'Because light physically imprints the image, photographs are often described as an "index" of their subject, unlike a painted or drawn representation.',
    ],
    [
      'The Bauhaus-affiliated concept of "New Vision" photography, associated with László Moholy-Nagy, emphasized what?',
      [
        'Unusual vantage points and experimental techniques revealing new ways of seeing',
        'A strict return to 19th-century portrait conventions',
        'Exclusive use of natural, unaltered light',
        'Rejection of all camera-based image-making',
      ],
      0,
      'Moholy-Nagy championed photograms, extreme angles, and multiple exposures to push photography beyond conventional representation.',
    ],
    [
      'What distinguishes a photograph\'s "studium," in Barthes\'s terminology, from its "punctum"?',
      [
        'Studium is general cultural or informational interest shared by any viewer',
        'Studium is the emotional wound only one viewer feels',
        "Studium refers only to a photograph's technical exposure settings",
        "Studium describes a photograph's monetary value",
      ],
      0,
      'Studium is the broad, culturally coded meaning a viewer can discuss and analyze; punctum is the private, unanalyzable detail that pierces them personally.',
    ],
    [
      "Rosalind Krauss's writing on photography frequently applied which broader theoretical framework to the medium?",
      [
        'Structuralist and post-structuralist theory',
        'Marxist economic determinism, exclusively',
        'Freudian dream analysis, exclusively',
        'Classical Aristotelian aesthetics',
      ],
      0,
      'Krauss drew on structuralist and post-structuralist thought, notably discussing photography\'s indexical nature and its relation to the "originality" of the avant-garde.',
    ],
    [
      "What was significant about Ansel Adams and Edward Weston's approach relative to gallery and museum acceptance of photography as fine art?",
      [
        'They helped push institutions to treat prints as unique, collectible art objects',
        'They refused to ever sell their prints',
        'They insisted photography could never equal painting as art',
        'They abandoned photography for painting later in life',
      ],
      0,
      "Their meticulous printing and advocacy, partly through the f/64 Group and Adams's later technical writing, helped establish photographic prints as objects worthy of fine-art status and value.",
    ],
    [
      'What is a "vintage print" in photographic connoisseurship and the art market?',
      [
        'A print made by the photographer at or near the time the image was captured',
        'Any black-and-white print, regardless of when it was made',
        'A print made using only 19th-century chemical processes',
        "A reproduction authorized after the photographer's death",
      ],
      0,
      "Vintage prints are valued above later reprints because they're considered closest to the photographer's original intent and historical moment.",
    ],
    [
      "How did Walter Benjamin's concept of \"aura\" complicate photography's status relative to earlier fine art media like painting?",
      [
        "Photography's infinite reproducibility undermined the idea of a singular, authentic original",
        'Photography instantly gained more aura than painting due to its realism',
        'Aura only applies to sculpture, not two-dimensional media',
        'Benjamin argued photography had no relationship to aura at all',
      ],
      0,
      'Because photographs can be reproduced identically and endlessly, Benjamin saw the medium as central to the broader erosion of "aura" in modern art.',
    ],
    [
      "What curatorial argument underpinned Szarkowski's broader project at MoMA's photography department?",
      [
        'Photography deserved to be studied on its own formal terms, not as a lesser cousin of painting',
        'Photography should always be displayed alongside its source negatives',
        'Only anonymous or amateur photography belonged in a museum',
        'Photographs should never be printed larger than the original negative',
      ],
      0,
      "Szarkowski's curatorial mission was to define and defend photography's own aesthetic vocabulary, distinct from criteria borrowed from painting or sculpture.",
    ],
  ],

  professor: [
    [
      'Andreas Gursky\'s photograph "Rhein II" set a benchmark in the art market by becoming what?',
      [
        'The most expensive photograph ever sold at auction at the time of its 2011 sale',
        'The first photograph acquired by the Louvre',
        'The first color photograph exhibited at MoMA',
        'The only photograph ever included in a major painting retrospective',
      ],
      0,
      "Gursky's digitally altered, minimalist image of the Rhine sold for over $4 million in 2011, symbolizing photography's arrival in blue-chip contemporary art markets.",
    ],
    [
      'What theoretical objection does Rosalind Krauss raise regarding photographic "originality" within the avant-garde?',
      [
        "Photography's endless reproducibility undermines claims to a singular avant-garde \"original\"",
        'Photography always produces a single unrepeatable original, like a painting',
        'Originality is irrelevant to any discussion of photographic theory',
        'Krauss argues photography predates the very concept of originality',
      ],
      0,
      "Krauss argued the avant-garde's myth of originality collides with photography's mechanically reproducible nature, since no single print holds the unique authorial status a painted canvas does.",
    ],
    [
      'Allan Sekula\'s critical essay "The Body and the Archive" examines the historical use of photography for what institutional purpose?',
      [
        'Criminal identification and social surveillance in the 19th century',
        'Royal portraiture commissioned by European monarchies',
        'Early color photography experiments',
        'Advertising campaigns for consumer goods',
      ],
      0,
      "Sekula traced how photography became entangled with policing and pseudo-scientific classification, notably through Alphonse Bertillon's identification system.",
    ],
    [
      "In photographic theory, what is the significance of applying Peirce's semiotic category of the \"index\" to a photograph?",
      [
        'It frames the photograph as physically caused by, and thus evidentially tied to, its referent',
        "It refers only to a photograph's place in a museum's cataloging system",
        'It describes photographs that depict indexes or lists as their subject matter',
        'It is a term used exclusively for digital, not analog, photography',
      ],
      0,
      'Charles Sanders Peirce\'s semiotics distinguishes the "index" (a sign physically connected to its object) from icon and symbol; photography\'s chemical or optical causation is often cited as paradigmatically indexical.',
    ],
    [
      'How did Geoffrey Batchen\'s scholarship complicate the standard "invention of photography" narrative centered on Talbot and Daguerre?',
      [
        'He documented numerous lesser-known figures independently pursuing photographic processes before 1839',
        'He argued photography was never actually invented but always existed as a natural phenomenon',
        'He credited the invention entirely to Nicéphore Niépce alone',
        'He claimed no photographic processes existed before 1850',
      ],
      0,
      'Batchen\'s "Burning with Desire" traced a broader, more diffuse history of photographic experimentation across Europe, unsettling the tidy two-inventor narrative.',
    ],
    [
      'What critical distinction does Sekula\'s essay "The Traffic in Photographs" draw regarding photography\'s supposed neutrality?',
      [
        "Photographic meaning is shaped by the institutional and economic contexts circulating the image",
        'Photographs are entirely free of ideological content by their mechanical nature',
        'Only staged photographs carry ideological meaning; unstaged ones are neutral',
        'Photographic meaning is fixed permanently at the moment of capture',
      ],
      0,
      "Sekula argued that a photograph's meaning shifts depending on where and how it circulates, whether in a newspaper, a gallery, or an archive, undermining claims of inherent neutrality.",
    ],
    [
      'What was art-historically significant about the 1978 exhibition "Mirrors and Windows," curated by John Szarkowski?',
      [
        'It framed postwar American photography along a spectrum from self-expression to documentary observation',
        'It was the first exhibition to exclude all color photography',
        'It exclusively displayed 19th-century daguerreotypes',
        "It marked photography's removal from MoMA's permanent collection",
      ],
      0,
      'Szarkowski\'s "mirrors" (personal, expressive) versus "windows" (observational, documentary) framework became a widely cited lens for categorizing postwar photographic practice.',
    ],
    [
      'Christopher Phillips\'s essay "The Judgment Seat of Photography" critiques which aspect of MoMA\'s historical role in the medium?',
      [
        "Its curators' outsized power to define photography's canon and value",
        'Its refusal to ever exhibit photography as art',
        'Its exclusive focus on European photographers',
        'Its early adoption of digital photography over film',
      ],
      0,
      "Phillips argued MoMA's photography curators, especially Steichen and Szarkowski, effectively acted as gatekeepers deciding which photographic practices counted as art.",
    ],
    [
      'What does Ariella Azoulay\'s concept of "the civil contract of photography" propose about the relationship between photographer, subject, and viewer?',
      [
        'Photography creates an ongoing civic relationship of mutual obligation among all three parties',
        'Only the photographer holds any rights or obligations regarding an image',
        "The subject's consent is legally required before any photograph can exist",
        'The concept applies exclusively to photographs of political leaders',
      ],
      0,
      "Azoulay argued photography establishes a shared civic space where photographed subjects retain a claim on how their image is used and understood, beyond simple ownership by the photographer.",
    ],
    [
      'How did the "Pictures Generation" artists of the late 1970s–80s, including Cindy Sherman and Richard Prince, engage with photography\'s relationship to mass media?',
      [
        'They appropriated and restaged existing media imagery to critique representation itself',
        'They rejected all use of appropriated or borrowed imagery',
        'They worked exclusively with 19th-century photographic processes',
        'They focused only on rural, non-media-saturated subject matter',
      ],
      0,
      'The Pictures Generation used strategies of appropriation and restaging to interrogate how mass media constructs identity, gender, and desire.',
    ],
    [
      "What argument underlies Vilém Flusser's philosophy of photography regarding the camera as an \"apparatus\"?",
      [
        'Photographers operate within a programmed set of possibilities dictated by the camera itself',
        'The camera is a fully neutral tool with no influence over the resulting image',
        'Only mechanical, non-electronic cameras qualify as apparatuses',
        "Flusser argued cameras eliminate the photographer's agency entirely",
      ],
      0,
      'Flusser proposed that cameras encode a finite "program" of possible images, and photographers largely explore combinations already latent within that program rather than acting with total freedom.',
    ],
    [
      "What is the critical significance of the 2007 rediscovery and subsequent posthumous exhibition of Vivian Maier's negatives, as debated among photography historians?",
      [
        'It raised questions about authorship, canon-formation, and who gets recognized as an artist',
        "It resolved a decades-long authentication dispute over Ansel Adams's negatives",
        'It was the first instance of street photography ever documented',
        'It proved that all major 20th-century photographers worked anonymously',
      ],
      0,
      "Because Maier never sought publication or gallery representation, her posthumous fame forced scholars to reconsider how institutional validation shapes who becomes part of the photographic canon.",
    ],
    [
      "In discussions of digital-era photographic ontology, what challenge does image manipulation software pose to Bazin's and Barthes's theories of photographic indexicality?",
      [
        'It destabilizes the assumed causal, unaltered link between image and referent that grounded earlier theory',
        'It has no bearing on indexicality, since digital files are also chemically produced',
        'It strengthens indexicality by making images more precise',
        'It only affects moving images, not still photographs',
      ],
      0,
      "Classical theories rested on photography's direct physical or chemical trace of reality; pervasive digital editing complicates the assumption that a photograph is straightforwardly caused by what it depicts.",
    ],
    [
      'What curatorial and critical stakes were raised by the Deutsche Börse Photography Prize and similar contemporary art prizes regarding photography\'s institutional status?',
      [
        "They signal photography's full integration into contemporary fine-art award and market structures",
        "They exist specifically to exclude photography from broader art-world recognition",
        'They were established to fund photographic equipment for students',
        'They are awarded only to photojournalists covering war',
      ],
      0,
      'Prizes like the Deutsche Börse Photography Prize situate photography within the same prestige economy as painting and sculpture, awarding critical and market recognition.',
    ],
    [
      'How does Susan Sontag\'s later work "Regarding the Pain of Others" revise or complicate arguments she made in "On Photography"?',
      [
        'She reconsiders her earlier skepticism, granting that images of suffering can still provoke genuine ethical response',
        'She fully retracts every claim made in "On Photography"',
        'She argues war photography should never be published under any circumstance',
        'She claims photographs of suffering have no effect on viewers whatsoever',
      ],
      0,
      'Written decades later, "Regarding the Pain of Others" is often read as Sontag revisiting and complicating, though not simply reversing, her earlier arguments about photography\'s capacity to desensitize viewers.',
    ],
  ],
};
